
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pixel } from "./types";
import { useState } from "react";
import { toast } from "sonner";
import { Loader } from "lucide-react";

interface PixelDeleteAlertProps {
  pixel: Pixel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
}

export const PixelDeleteAlert = ({ 
  pixel, 
  open, 
  onOpenChange, 
  onConfirm 
}: PixelDeleteAlertProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgPixel = pixel?.organization_id ? true : false;
  
  const handleConfirm = async () => {
    console.log("DEBUG: Delete button clicked in confirmation dialog");
    console.log("DEBUG: Pixel being deleted:", pixel ? JSON.stringify(pixel) : "null");
    setIsDeleting(true);
    try {
      console.log("DEBUG: Calling onConfirm handler from PixelDeleteAlert");
      await onConfirm();
      console.log("DEBUG: onConfirm handler completed successfully");
      // Only close the dialog after successful deletion
      onOpenChange(false);
    } catch (error) {
      console.error("DEBUG: Error in pixel deletion (from AlertDialog):", error);
      toast.error(`Error deleting pixel: ${error.message || "Unknown error"}`);
    } finally {
      console.log("DEBUG: Resetting isDeleting state");
      setIsDeleting(false);
    }
  };
  
  return (
    <AlertDialog open={open} onOpenChange={(value) => {
      if (isDeleting) {
        console.log("DEBUG: Preventing dialog close while deletion in progress");
        return;
      }
      console.log("DEBUG: Alert dialog onOpenChange called with value:", value);
      onOpenChange(value);
    }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the pixel "{pixel?.name}" {isOrgPixel && "from your organization"} and all its associated data.
            This action cannot be undone.
            {isOrgPixel && (
              <p className="mt-2 font-medium text-amber-600">
                Note: Only organization owners can delete pixels associated with an organization.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault(); // Prevent the default action which would close the dialog
              handleConfirm();
            }}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? (
              <span className="flex items-center gap-2">
                <Loader className="h-4 w-4 animate-spin" />
                Deleting...
              </span>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
